/**
 * The View-as lifecycle (M4S-5670, docs/pdlc/cs-view-as-client.md §4).
 *
 * Start, switch, Clear and end are all full page reloads, so no stale client
 * state can survive them in DANI, react-query caches or any other provider.
 * Resume happens on every full page load of a tab that holds a session id,
 * before any client state exists. While active, the tab re-checks its session
 * when it becomes visible, every 5 minutes, and before any write or DANI send
 * whose last good check is older than 60 seconds.
 *
 * Only Studio's own View-as checks end View-as: a refusal from
 * `/api/view-as/*` with a `view_as_*` code, a network failure past the
 * tolerance, or the 12h expiry. An API 403 (credits included) never does.
 *
 * Non-React on purpose: the axios, GraphQL and fetch backstops and the DANI
 * send guard call `ensureViewAsFresh` and `recordViewAsAction` directly.
 * `SubsidiaryFilterProvider` drives the timers and the UI.
 */

import { toast } from 'react-toastify';

import { discardReportsLaunch } from '@/@reports/utils/kickoff';
import { writeStoredCreditGate } from '@/@studio-ai/utils/credit-gate-storage';
import {
    clearViewAsEntry,
    readViewAsEntry,
    writeViewAsEndReason,
    writeViewAsEntry,
} from '@/@subsidiary/view-as-storage';
import {
    getViewAsSnapshot,
    IDLE_VIEW_AS,
    patchViewAsSnapshot,
    setViewAsSnapshot,
} from '@/@subsidiary/view-as-store';
import {
    currentPagePath,
    isViewAsSessionCode,
    postViewAsEvent,
    sendViewAsAction,
    sendViewAsRefusal,
    type ViewAsCallResult,
} from '@/lib/view-as/client';
import {
    VIEW_AS_FRESHNESS_MS,
    VIEW_AS_NETWORK_TOLERANCE_MS,
    VIEW_AS_STOP_TIMEOUT_MS,
    VIEW_AS_WRITE_THROTTLE_MS,
} from '@/lib/view-as/constants';
import type { ViewAsAction, ViewAsSessionResponse } from '@/lib/view-as/types';

interface LifecycleDeps {
    reload: () => void;
    now: () => number;
}

let deps: LifecycleDeps = {
    reload: () => window.location.reload(),
    now: () => Date.now(),
};

/** Test seam. */
export function setViewAsLifecycleDepsForTests(next: Partial<LifecycleDeps>): void {
    deps = { ...deps, ...next };
}

/** Test seam. */
export function resetViewAsLifecycleForTests(): void {
    inflightCheck = null;
    throttle.clear();
    for (const timer of throttleTimers.values()) clearTimeout(timer);
    throttleTimers.clear();
}

/** Keys that must never carry over between clients: DANI's credit-gate pause and a pending report launch. */
function clearSideKeys(): void {
    try {
        writeStoredCreditGate(null);
    } catch {
        // storage unavailable
    }
    discardReportsLaunch();
}

function activeSnapshot(data: ViewAsSessionResponse) {
    return {
        sessionId: data.sessionId,
        orgId: data.client.orgId,
        sfAccountId: data.client.sfAccountId,
        orgName: data.client.orgName,
        status: 'active' as const,
        lastVerifiedAt: deps.now(),
        expiresAt: Date.parse(data.expiresAt),
    };
}

function failureMessage(result: Exclude<ViewAsCallResult<unknown>, { ok: true }>): string {
    return result.kind === 'refused' ? result.message : 'Studio could not reach View as.';
}

/**
 * The end path: refuse everything from now on, drop the tab's session, leave a
 * one-shot reason for after the reload, and reload as the staff member.
 * Idempotent: a second caller while ending does nothing.
 */
export function endViewAs(reason: string): void {
    const s = getViewAsSnapshot();
    if (s.status === 'ending') return;
    patchViewAsSnapshot({ status: 'ending' });
    clearViewAsEntry();
    clearSideKeys();
    writeViewAsEndReason(reason);
    deps.reload();
}

/** Best-effort stop, awaited at most `VIEW_AS_STOP_TIMEOUT_MS`. */
async function stopBestEffort(sessionId: string, reason: string): Promise<void> {
    await Promise.race([
        postViewAsEvent(
            { event: 'stop', sessionId, reason, pagePath: currentPagePath() },
            { keepalive: true },
        ).catch(() => undefined),
        new Promise((resolve) => setTimeout(resolve, VIEW_AS_STOP_TIMEOUT_MS)),
    ]);
}

/**
 * Start (or switch to) a client. The server writes the start row first; only
 * then does the tab store the session id and reload. On failure the tab stays
 * exactly as it was, including on the old client during a switch.
 */
export async function startViewAs(
    staffUserId: string,
    client: { sfAccountId: string; orgId?: number },
): Promise<boolean> {
    const previous = getViewAsSnapshot();
    const result = await postViewAsEvent({
        event: 'start',
        clientSfAccountId: client.sfAccountId,
        clientOrgId: client.orgId,
        pagePath: currentPagePath(),
    });
    if (!result.ok) {
        toast.error(`Could not start View as: ${failureMessage(result)}`);
        return false;
    }
    if (!writeViewAsEntry(staffUserId, result.data.sessionId)) {
        toast.error('Could not start View as: this tab cannot store its session.');
        void stopBestEffort(result.data.sessionId, 'storage_unavailable');
        return false;
    }
    patchViewAsSnapshot({ status: 'starting' });
    if (previous.sessionId && previous.sessionId !== result.data.sessionId) {
        await stopBestEffort(previous.sessionId, 'switch');
    }
    clearSideKeys();
    deps.reload();
    return true;
}

/** Clear: a best-effort stop, then drop the session and reload as the staff member. */
export async function stopViewAs(reason = 'clear'): Promise<void> {
    const s = getViewAsSnapshot();
    patchViewAsSnapshot({ status: 'ending' });
    if (s.sessionId) await stopBestEffort(s.sessionId, reason);
    clearViewAsEntry();
    clearSideKeys();
    deps.reload();
}

/**
 * Resume on a full page load. Returns the verified session, or null when the
 * tab has none or the server refused it. A refused resume drops the session and
 * the tab renders as the staff member; no client state was applied, so no
 * reload is needed, and the notice is shown here.
 */
export async function resumeViewAs(staffUserId: string): Promise<ViewAsSessionResponse | null> {
    const entry = readViewAsEntry(staffUserId);
    if (!entry) return null;
    setViewAsSnapshot({ ...IDLE_VIEW_AS, sessionId: entry.sessionId, status: 'restoring' });
    const result = await postViewAsEvent({
        event: 'resume',
        sessionId: entry.sessionId,
        pagePath: currentPagePath(),
    });
    if (result.ok) {
        setViewAsSnapshot(activeSnapshot(result.data));
        return result.data;
    }
    clearViewAsEntry();
    clearSideKeys();
    setViewAsSnapshot(IDLE_VIEW_AS);
    toast.error(`View as ended: ${failureMessage(result)}`);
    return null;
}

/**
 * Re-check an active session. A server refusal ends View-as; a network error
 * keeps the last good state for at most `VIEW_AS_NETWORK_TOLERANCE_MS`.
 * Resolves true while the tab may keep acting as the client.
 */
export async function checkViewAs(): Promise<boolean> {
    const s = getViewAsSnapshot();
    if (!s.sessionId || s.status !== 'active') return false;
    if (s.expiresAt != null && deps.now() >= s.expiresAt) {
        endViewAs('the session reached its 12-hour limit.');
        return false;
    }
    const result = await postViewAsEvent({ event: 'check', sessionId: s.sessionId });
    const current = getViewAsSnapshot();
    if (current.sessionId !== s.sessionId || current.status !== 'active') return false;
    if (result.ok) {
        patchViewAsSnapshot({
            lastVerifiedAt: deps.now(),
            expiresAt: Date.parse(result.data.expiresAt),
        });
        return true;
    }
    if (result.kind === 'refused') {
        endViewAs(result.message);
        return false;
    }
    const lastGood = current.lastVerifiedAt ?? 0;
    if (deps.now() - lastGood > VIEW_AS_NETWORK_TOLERANCE_MS) {
        endViewAs('Studio could not confirm the session for 10 minutes.');
        return false;
    }
    return true;
}

let inflightCheck: Promise<boolean> | null = null;

/**
 * Before a write or a DANI send. True when the tab holds no session (nothing to
 * check) or holds an active one checked within `VIEW_AS_FRESHNESS_MS` (or
 * re-checked now). False when the session is not active or the check ended it.
 * Concurrent callers share one check.
 */
export function ensureViewAsFresh(): Promise<boolean> {
    const s = getViewAsSnapshot();
    if (!s.sessionId) return Promise.resolve(true);
    if (s.status !== 'active') return Promise.resolve(false);
    if (s.expiresAt != null && deps.now() >= s.expiresAt) {
        endViewAs('the session reached its 12-hour limit.');
        return Promise.resolve(false);
    }
    if (s.lastVerifiedAt != null && deps.now() - s.lastVerifiedAt < VIEW_AS_FRESHNESS_MS) {
        return Promise.resolve(true);
    }
    if (!inflightCheck) {
        inflightCheck = checkViewAs().finally(() => {
            inflightCheck = null;
        });
    }
    return inflightCheck;
}

// ---------------------------------------------------------------------------
// Recording
// ---------------------------------------------------------------------------

const throttle = new Map<string, { sentAt: number; pending: number }>();
const throttleTimers = new Map<string, ReturnType<typeof setTimeout>>();

function handleRecordResult(result: ViewAsCallResult<unknown>): void {
    if (!result.ok && result.kind === 'refused' && isViewAsSessionCode(result.code)) {
        endViewAs(result.message);
    } else if (!result.ok) {
        console.warn('[view-as] action row not written', result);
    }
}

function send(action: ViewAsAction, ref: string | null, detail?: Record<string, unknown>): void {
    const s = getViewAsSnapshot();
    if (!s.sessionId || s.status !== 'active') return;
    void sendViewAsAction(s.sessionId, action, ref, detail).then(handleRecordResult);
}

/**
 * Record an allowed action against the tab's session. Fail-open: a lost row is
 * logged and never blocks the action. `api_write` rows are throttled to one per
 * ref per `VIEW_AS_WRITE_THROTTLE_MS` per tab, and the next row carries
 * `detail.count`, so autosave does not write a row a second while the count
 * stays right. Every other action is written each time (the server dedupes
 * `dani_conversation` and `report_run`).
 */
export function recordViewAsAction(
    action: ViewAsAction,
    ref: string | null | undefined,
    detail: Record<string, unknown> = {},
): void {
    const s = getViewAsSnapshot();
    if (!s.sessionId || s.status !== 'active') return;
    const key = ref ?? '';
    if (action !== 'api_write') {
        send(action, key || null, detail);
        return;
    }

    const now = deps.now();
    const entry = throttle.get(key);
    if (!entry || now - entry.sentAt >= VIEW_AS_WRITE_THROTTLE_MS) {
        const count = 1 + (entry?.pending ?? 0);
        throttle.set(key, { sentAt: now, pending: 0 });
        const timer = throttleTimers.get(key);
        if (timer) {
            clearTimeout(timer);
            throttleTimers.delete(key);
        }
        send(action, key || null, count > 1 ? { ...detail, count } : detail);
        return;
    }

    entry.pending += 1;
    if (!throttleTimers.has(key)) {
        const wait = Math.max(0, VIEW_AS_WRITE_THROTTLE_MS - (now - entry.sentAt));
        throttleTimers.set(
            key,
            setTimeout(() => {
                throttleTimers.delete(key);
                const pending = throttle.get(key)?.pending ?? 0;
                if (pending === 0) return;
                throttle.set(key, { sentAt: deps.now(), pending: 0 });
                send(action, key || null, { ...detail, count: pending });
            }, wait),
        );
    }
}

/** Record a refusal by a browser backstop, best-effort. */
export function recordViewAsRefusal(detail: Record<string, unknown>): void {
    const s = getViewAsSnapshot();
    if (!s.sessionId) return;
    void sendViewAsRefusal(s.sessionId, detail).then((result) => {
        if (!result.ok) console.warn('[view-as] refused row not written', result);
    });
}
